/**
 * Venture Service - Pure business logic functions
 *
 * This module contains pure functions for venture-related business logic,
 * including funding calculations, status transitions, and validation rules.
 * All functions are side-effect free and can be easily unit tested.
 */
/**
 * Calculate funding progress as a percentage
 *
 * @param fundsPledged - Current amount of funds pledged
 * @param targetGoal - Target funding goal
 * @returns Funding progress percentage (0-100), capped at 100
 *
 * Requirements: 2.3, 7.1
 */
export function calculateFundingProgress(fundsPledged, targetGoal) {
    // Handle edge case: zero or negative target goal
    if (targetGoal <= 0) {
        return 0;
    }
    const progress = (fundsPledged / targetGoal) * 100;
    // Cap at 100% even if overfunded
    return Math.min(progress, 100);
}
/**
 * Check if a venture has expired based on its end date
 *
 * @param endDate - ISO 8601 date string or null
 * @returns true if the venture has expired, false otherwise
 *
 * Requirements: 14.1
 */
export function isVentureExpired(endDate) {
    // No end date means no expiration
    if (!endDate) {
        return false;
    }
    const now = Date.now();
    const end = new Date(endDate).getTime();
    return now > end;
}
/**
 * Determine if a venture can be edited based on its status
 *
 * @param status - Current venture status
 * @returns true if the venture can be edited, false otherwise
 *
 * Requirements: 3.2, 3.3, 3.4
 *
 * Rules:
 * - draft: All fields can be edited
 * - live: Only pitchContent and description can be edited
 * - successful, failed, converted: No editing allowed
 */
export function canEditVenture(status) {
    return status === "draft" || status === "live";
}
/**
 * Validate if a venture can be published
 *
 * @param venture - The venture to validate
 * @returns true if the venture can be published, false otherwise
 *
 * Requirements: 2.2, 3.1
 *
 * A venture can be published if:
 * - Status is 'draft'
 * - Has a name
 * - Has pitch content
 * - Has a positive target goal
 */
export function canPublishVenture(venture) {
    return (venture.status === "draft" &&
        !!venture.name &&
        !!venture.pitchContent &&
        !!venture.targetGoal &&
        venture.targetGoal > 0);
}
/**
 * Determine if a venture should transition to 'successful' status
 *
 * @param fundsPledged - Current amount of funds pledged
 * @param targetGoal - Target funding goal
 * @returns true if funding goal is met or exceeded
 *
 * Requirements: 2.3, 7.1
 */
export function shouldTransitionToSuccessful(fundsPledged, targetGoal) {
    return fundsPledged >= targetGoal && targetGoal > 0;
}
/**
 * Determine if a venture should transition to 'failed' status
 *
 * @param endDate - ISO 8601 date string or null
 * @param fundsPledged - Current amount of funds pledged
 * @param targetGoal - Target funding goal
 * @returns true if the venture has expired without meeting its goal
 *
 * Requirements: 14.1
 */
export function shouldTransitionToFailed(endDate, fundsPledged, targetGoal) {
    // Can only fail if there's an end date
    if (!endDate) {
        return false;
    }
    const expired = isVentureExpired(endDate);
    const goalNotMet = fundsPledged < targetGoal;
    return expired && goalNotMet;
}
